import React, { useState, useEffect } from 'react';
import { X, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';

interface Friend {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface FriendsListDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

function FriendsListDialog({ isOpen, onClose, userId }: FriendsListDialogProps) {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchFriends();
    }
  }, [isOpen, userId]);

  const fetchFriends = async () => {
    try {
      // Get accepted friend requests where the user is either sender or receiver
      const { data: requests, error: requestsError } = await supabase
        .from('friend_requests')
        .select(`
          sender:users!friend_requests_sender_id_fkey(
            id,
            username,
            display_name,
            avatar_url
          ),
          receiver:users!friend_requests_receiver_id_fkey(
            id,
            username,
            display_name,
            avatar_url
          )
        `)
        .eq('status', 'accepted')
        .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`);

      if (requestsError) throw requestsError;

      // Transform the data to get a list of friends
      const friendsList = (requests || []).map(request => {
        // If the user is the sender, return the receiver as friend, and vice versa
        return request.sender.id === userId ? request.receiver : request.sender;
      });

      setFriends(friendsList);
    } catch (error) {
      console.error('Error fetching friends:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Friends</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : friends.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No friends yet</p>
              <p className="text-sm text-gray-500 mt-2">
                Add friends to see them here
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {friends.map((friend) => (
                <Link
                  key={friend.id}
                  to={`/profile/${friend.id}`}
                  onClick={onClose}
                  className="flex items-center space-x-4 p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                    {friend.avatar_url ? (
                      <img
                        src={friend.avatar_url}
                        alt={friend.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium">
                      {friend.display_name || friend.username}
                    </h4>
                    <p className="text-sm text-gray-600">
                      @{friend.username}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FriendsListDialog;